import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionCardWrapper,
  ActionCardHeader,
  ActionCardBody,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  DescriptionPreview,
  InfoContent,
  InfoIconWrapper,
  ActionCardHeaderWrapper,
  ActionCardTitleBox,
  ActionCardTitle,
} from "./ActionCard.styles";
import { Box, Collapse, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { CoinsIcon, CrowdIcon, EmailIcon, SavingsIcon } from "../../../../assets/icons/Icons";
import DefaultIcon from "../../../../components/common/DefaultIcon";
import { GroupItem } from "../../../../types/groupItem";
import { ActionModule } from "../../../../types/actionModule/actionModule";

const ActionCard: FC<{
  actionItem: ActionModule;
  groupsList: GroupItem[];
  onEditClicked?: () => void;
}> = ({ actionItem, groupsList, onEditClicked }) => {
  const { t } = useTranslation();
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasExpandableDescription, setHasExpandableDescription] = useState(false);

  useEffect(() => {
    const el = descriptionRef.current;
    if (el) {
      setHasExpandableDescription(el.scrollWidth > el.clientWidth);
    }
  }, [actionItem.description]);

  return (
    <ActionCardWrapper onClick={actionItem.canBeEdited ? onEditClicked : undefined}>
      <ActionCardHeader>
        <ActionCardHeaderWrapper>
          <DefaultIcon
            iconName={actionItem.iconName}
            colorHex={actionItem.colorHex || ""}
            iconUrl={actionItem.iconUrl} />
          <Tooltip title={actionItem.name}>
            <ActionCardTitleBox>
              <ActionCardTitle variant="h6" noWrap>
                {actionItem.name}
              </ActionCardTitle>
            </ActionCardTitleBox>
          </Tooltip>
        </ActionCardHeaderWrapper>
      </ActionCardHeader>
      <ActionCardBody>
        <InfoGrid>
          <InfoItem>
            <InfoIconWrapper>
              <CoinsIcon />
            </InfoIconWrapper>
            <InfoContent>
              <Tooltip title={t("actionPage.maxReward")}>
                <InfoLabel>{t("actionPage.maxReward")}</InfoLabel>
              </Tooltip>
              <Tooltip title={actionItem.maxReward || t("none")}>
                <InfoValue>{actionItem.maxReward || t("none")}</InfoValue>
              </Tooltip>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIconWrapper>
              <SavingsIcon />
            </InfoIconWrapper>
            <InfoContent>
              <Tooltip title={t("actionPage.rewardType")}>
                <InfoLabel>{t("actionPage.rewardType")}</InfoLabel>
              </Tooltip>
              <Tooltip title={actionItem.rewardType || "-"}>
                <InfoValue>{actionItem.rewardType || "-"}</InfoValue>
              </Tooltip>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIconWrapper>
              <CrowdIcon />
            </InfoIconWrapper>
            <InfoContent>
              <Tooltip title={t("actionPage.maxUsers")}>
                <InfoLabel>{t("actionPage.maxUsers")}</InfoLabel>
              </Tooltip>
              <Tooltip title={actionItem.maxUserCanComplete ?? t("actionPage.noLimit")}>
                <InfoValue>{actionItem.maxUserCanComplete ?? t("actionPage.noLimit")}</InfoValue>
              </Tooltip>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIconWrapper>
              <EmailIcon />
            </InfoIconWrapper>
            <InfoContent>
              <Tooltip title={t("actionPage.emailNotificationAddress")}>
                <InfoLabel>{t("actionPage.emailNotificationAddress")}</InfoLabel>
              </Tooltip>
              <Tooltip title={actionItem.emailNotificationAddresses.length > 0
                  ? actionItem.emailNotificationAddresses.join(", ")
                  : t("actionPage.noEmail")}>
                <InfoValue>{actionItem.emailNotificationAddresses.length > 0
                  ? actionItem.emailNotificationAddresses.join(", ")
                  : "-"}</InfoValue>
              </Tooltip>
            </InfoContent>
          </InfoItem>

          {/* <InfoItem fullWidth>
            <InfoIconWrapper>
              <GroupIcon />
            </InfoIconWrapper>
            <InfoContent>
              <Tooltip title={t("actionPage.assignedGroups")}>
                <InfoLabel>{t("actionPage.assignedGroups")}</InfoLabel>
              </Tooltip>
              <Tooltip title={getAssignedGroupNames()}>
                <InfoValue>{getAssignedGroupNames()}</InfoValue>
              </Tooltip>
            </InfoContent>
          </InfoItem> */}
        </InfoGrid>

        <Divider sx={{ my: 1 }} />

        {actionItem.description && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
              {t("actionPage.actionDescription")}
            </Typography>
            {!expanded && (
              <DescriptionPreview ref={descriptionRef} variant="body2" noWrap>
                {actionItem.description}
              </DescriptionPreview>
            )}
            {hasExpandableDescription && (
              <>
                <Collapse
                  in={expanded}
                  timeout={300}
                  unmountOnExit
                  sx={{
                    transition: (theme) => theme.transitions.create("max-height", {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeOut,
                    }),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      padding: "8px 0",
                      transition: "padding 0.3s ease",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {actionItem.description}
                    </Typography>
                  </Box>
                </Collapse>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Tooltip title={expanded ? t("buttons.collapse") : t("buttons.expand")}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((prev) => !prev);
                      }}
                      sx={{
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        )}
      </ActionCardBody>
    </ActionCardWrapper>
  );
};

export default ActionCard;
